import { useLocation } from 'react-router';

import get from 'lodash/get';
import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../../Table/AsyncTableContainer';

import InfoComponent from '../../Tooltip/InfoComponent';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';
import { LINK_DISPLAY_NAME } from '../../Tooltip/constants';

import { history } from '../../../store';
import { secondaryColor } from '../../../theme';
import WithLink from '../../Linkify';
import StyledLink from '../../StyledLink';
import ListActions from '../../Table/ListActions';
import { multiBuildingFilter } from '../../Table/filters';

const ExternalLinksComponent = (props) => {
  const { t } = useTransl();
  const location = useLocation();
  const {
    onLoad,
    items,
    getItems,
    listParams,
    listKey,
    deleteExternalAccount,
    buildingOptions,
  } = props;

  const columns = [
    {
      Header: t('title.internalName'),
      accessor: ({ name }) => name,
      id: 'name',
      headerClassName: 'sort',
    },
    {
      Header: (
        <Box display="flex" alignItems="center">
          <Typography fontWeight="bold">{t('title.displayName')}</Typography>
          <InfoComponent type={LINK_DISPLAY_NAME} isTooltip isOnHover />
        </Box>
      ),
      accessor: ({ displayName }) => displayName,
      id: 'display_name',
      headerClassName: 'sort',
    },
    {
      Header: t('title.url'),
      accessor: ({ url }) => <WithLink>{url}</WithLink>,
      id: 'url',
      sortable: false,
    },
    {
      Header: t('title.availableFor'),
      accessor: ({ availableBuildings }) =>
        availableBuildings.map((buildingId) => {
          const building = Object.values(buildingOptions).find(
            ({ id }) => id === buildingId,
          );

          return (
            <Box key={buildingId}>
              <StyledLink to={`/building/${building?.id}`}>
                {building?.name}
              </StyledLink>
            </Box>
          );
        }),
      id: 'availableBuildings',
      sortable: false,
    },
    {
      Header: t('title.actions'),
      id: 'actions',
      fixed: 'right',
      sortable: false,
      width: 120,
      accessor: ({ id }) => (
        <ListActions
          actions={[
            {
              label: t('actions.delete'),
              color: secondaryColor,
              onClick: () => deleteExternalAccount(id, listParams),
            },
          ]}
          isFullWidth
        />
      ),
    },
  ];

  const filters = [multiBuildingFilter(listParams, buildingOptions)];

  const defaultParams = {
    limit: 25,
    offset: 0,
    ordering: 'name',
    available_buildings: null,
  };

  const action = [
    {
      url: `${location.pathname}/create`,
      label: `${t('actions.create')} ${t('account.customLinks')}`,
    },
  ];

  const onRowClick = (rowInfo) => {
    const id = get(rowInfo, 'original.id', null);
    id && history.push(`${location.pathname}/${id}`);
  };

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.customIntegrationsTable}
      title={t('account.customLinks')}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.name}
      defaultSortDesc={false}
      manual
      filters={filters}
      showFiltersAreApplied={false}
      onLoad={onLoad}
      items={items}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      listKey={listKey}
      onRowClick={onRowClick}
      rowStyles={rowStyles}
      action={<ListActions actions={action} />}
    />
  );
};

ExternalLinksComponent.propTypes = {
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  deleteExternalAccount: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  buildingOptions: PropTypes.instanceOf(Object),
};

export default ExternalLinksComponent;
